import { useState } from 'react';
import { Boxes } from 'lucide-react';
import type { InventoryAppSettings } from '@quds/shared';
import { useAuth } from '@/auth/AuthContext';
import { Button, Card, CardBody, CardHeader, ConfirmDialog, FormField, Input, Spinner, StatusBadge } from '@/components/ui';
import {
  activateInventoryApp,
  deactivateInventoryApp,
  forceLogoutInventoryApp,
  getInventoryAppSettings,
  setInventoryAppPassword,
  useQuery,
} from '@/data';
import { errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

const key = () => crypto.randomUUID();

/** Batch A — GLOBAL Inventory App management (not per pharmacy). */
export function InventoryAppCard() {
  const { can } = useAuth();
  const canManage = can('inventory_app.manage');
  const settings = useQuery<InventoryAppSettings>(getInventoryAppSettings, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmForceLogout, setConfirmForceLogout] = useState(false);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(ok);
      await settings.reload();
    } catch (err) {
      setError(errorMessage(err, 'The action could not be completed.'));
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async () => {
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    await run(() => setInventoryAppPassword({ newPassword, idempotencyKey: key() }), 'Inventory App password changed.');
    setNewPassword('');
    setConfirmPassword('');
  };

  const isActive = settings.data?.isActive === true;

  return (
    <Card>
      <CardHeader
        title="Inventory App — global singleton"
        icon={<Boxes size={16} aria-hidden="true" />}
        description="Platform-wide credentials and session control. There is only one Inventory App; it is not configured per business."
        actions={
          <Button variant="ghost" size="sm" onClick={settings.reload} disabled={settings.loading || settings.refreshing}>
            Refresh
          </Button>
        }
      />
      <CardBody>
        {settings.loading ? (
          <p className="flex items-center gap-2 text-[13px] text-ink-secondary">
            <Spinner size={16} /> Loading Inventory App status…
          </p>
        ) : settings.error ? (
          <p className="text-[13px] text-ink-secondary">Could not load status: {settings.error.message}</p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 text-[13px] text-ink-secondary">
              <StatusBadge tone={isActive ? 'success' : 'warning'} label={isActive ? 'Active' : 'Inactive'} />
              <StatusBadge tone="info" label="Global" dot={false} />
              {settings.data?.updatedAt && <span>Updated {formatDateTime(settings.data.updatedAt)}</span>}
              {settings.data?.lastForceLogoutAt && <span>Last force logout {formatDateTime(settings.data.lastForceLogoutAt)}</span>}
            </div>

            {error && <p role="alert" className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-[13px] text-danger">{error}</p>}
            {notice && <p role="status" className="rounded-md border border-success/30 bg-success-soft px-3 py-2 text-[13px] text-success">{notice}</p>}

            {canManage ? (
              <div className="space-y-5">
                <div className="flex flex-wrap gap-2">
                  <Button variant="primary" disabled={busy || isActive} onClick={() => run(() => activateInventoryApp({ idempotencyKey: key() }), 'Inventory App activated.')}>
                    Activate
                  </Button>
                  <Button variant="secondary" disabled={busy || !isActive} onClick={() => run(() => deactivateInventoryApp({ idempotencyKey: key() }), 'Inventory App deactivated.')}>
                    Deactivate
                  </Button>
                  <Button variant="secondary" disabled={busy} onClick={() => setConfirmForceLogout(true)}>
                    Force logout all
                  </Button>
                </div>

                <fieldset className="grid gap-3 sm:grid-cols-2">
                  <legend className="mb-1 text-sm font-medium text-ink">Change password</legend>
                  <FormField label="New password" htmlFor="inv-new">
                    <Input id="inv-new" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                  </FormField>
                  <FormField label="Confirm password" htmlFor="inv-confirm">
                    <Input id="inv-confirm" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                  </FormField>
                  <div className="sm:col-span-2">
                    <Button variant="secondary" disabled={busy || !newPassword} onClick={() => void changePassword()}>
                      Change password
                    </Button>
                  </div>
                </fieldset>
              </div>
            ) : (
              <p className="text-[13px] text-ink-secondary">View only — your role cannot manage the Inventory App.</p>
            )}
          </div>
        )}
      </CardBody>

      <ConfirmDialog
        open={confirmForceLogout}
        title="Force logout all Inventory App sessions?"
        message="Every signed-in Inventory App user will be signed out and must log in again."
        confirmLabel="Force logout"
        onCancel={() => setConfirmForceLogout(false)}
        onConfirm={() => {
          setConfirmForceLogout(false);
          void run(() => forceLogoutInventoryApp({ idempotencyKey: key() }), 'All Inventory App sessions were signed out.');
        }}
      />
    </Card>
  );
}
